import "../locales";
import * as React from "react";
import { PostHogProvider } from "posthog-js/react";
import { Outlet } from "react-router-dom";
import { useUserSlice } from "@/models/useUserSlice";
import { useUISlice } from "@/models/useUISlice";
import AlertWidgetSelectAIModel from "@/ui/AlertWidgetSelectAIModel";
import AlertModal from "@/ui/AlertModal";
import { useToast } from "@/models/useToast";
import AlertPrompt from "@/ui/AlertPrompt";
import AlertSelect from "@/ui/AlertSelect";
import AlertWidgetPaywall from "@/ui/AlertWidgetPaywall";
import AlertPromptSignIn from "@/ui/AlertPromptSignIn";
import { MOBILE_MAX_WIDTH } from "@/constants";
import { SafeArea } from "capacitor-plugin-safe-area";

const HelmetProvider = React.lazy(() =>
  import("react-helmet-async").then((m) => ({ default: m.HelmetProvider }))
);

const GoogleOAuthProvider = React.lazy(() =>
  import("@react-oauth/google").then((m) => ({
    default: m.GoogleOAuthProvider,
  }))
);

// Lazy load analytics
const loadAnalytics = () => {
  import("@/fetchData/datadog");
  import("@/fetchData/gtag").then((gtag) => {
    gtag.pageview(window.location.pathname);
  });
};

export default function MyApp() {
  const _hydrated = useUserSlice((state) => state._hasHydrated);
  const { ToastContainer } = useToast();

  React.useEffect(() => {
    loadAnalytics();

    const handleResize = async () => {
      const { insets } = await SafeArea.getSafeAreaInsets();
      useUISlice.getState().setDevice({
        isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
        width: window.innerWidth,
        height: window.innerHeight,
        safeArea: { top: insets.top, bottom: insets.bottom },
      });
    };

    handleResize();
    const handlePopState = () => {
      loadAnalytics();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!_hydrated) {
    return null;
  }

  return (
    <React.Suspense>
      <HelmetProvider>
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={{
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
            disable_session_recording: false,
          }}
        >
          <GoogleOAuthProvider
            clientId={import.meta.env.VITE_PUBLIC_GOOGLE_CLIENT_ID}
          >
            <Outlet />
            <AlertModal />
            <AlertPrompt />
            <ToastContainer />
            <AlertSelect />
            <AlertWidgetPaywall />
            <AlertWidgetSelectAIModel />
            <AlertPromptSignIn />
          </GoogleOAuthProvider>
        </PostHogProvider>
      </HelmetProvider>
    </React.Suspense>
  );
}
